import { EventConditionEditor } from "@/features/events/components/EventConditionEditor";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/ui/primitives/collapsible";
import { EventTypesButton } from "@/features/events/components/EventTypesButton";
import { useNotify } from "@/common/notifications";
import Button from "@imminently/decisively-ui/Button";
import SwitchField from "@imminently/decisively-ui/SwitchField";
import FormPanel from "@imminently/decisively-ui/FormPanel";
import Dialog from "@imminently/decisively-ui/Dialog";
import EmptyOverlay from "@imminently/decisively-ui/EmptyOverlay";
import ItemList from "@imminently/decisively-ui/ItemList";
import { DialogHeader } from "@/ui/dialogs/DialogHeader";
import SelectField from "@imminently/decisively-ui/SelectField";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import TextArea from "@imminently/decisively-ui/TextArea";
import TextField from "@imminently/decisively-ui/TextField";
import { Check, ChevronDown, Pencil, Plus, Trash2 } from "lucide-react";
import { EventIcon } from "@/features/events/components/EventIcon";
import { AdapterResourceOwnership } from "@/features/adapters/components/AdapterResourceOwnership";
import type { Release } from "@packages/commons";
import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useWatch } from "react-hook-form";
import { useLocalStorage } from "react-use";
import { ENABLE_EVENT_CONNECTOR_CONDITIONS, CONNECTABLE_EVENT_TYPES, getEventTypeDisplayName, validateEventConditions, type EventCondition } from "@packages/commons";
import { type EventConnector, useSettings } from "../SettingsContext";
import { createConnector, deleteConnector, testConnector, updateConnector } from "../settings.api";
import { getActiveTenancy } from "@/features/tenancy";

type ConnectorType = "slack" | "webhook" | "email" | "teams" | "clickup";
type SupportedEvent = string;

const supportedEvents = CONNECTABLE_EVENT_TYPES;
const recommendedEvents = supportedEvents.filter(event => event.recommendedConnectable);
const otherEvents = supportedEvents.filter(event => !event.recommendedConnectable);
const supportedEventGroups = [...new Set(recommendedEvents.map(event => event.category))].map(category => ({
  category,
  label: category === "fileProcessing" ? "File processing" : category === "orgmodels" ? "Organisation models" : category,
  events: recommendedEvents.filter(event => event.category === category),
}));
interface ConnectorDraft {
  type: ConnectorType;
  name: string;
  webhookUrl: string;
  emailToText: string;
  feedbackEmailToText: string;
  clickUpToken: string;
  clickUpListId: string;
  slackBotToken: string;
  slackChannelId: string;
  subscribedEvents: SupportedEvent[];
  conditions?: EventCondition | null;
}

const connectorTypeOptions: Array<{ value: ConnectorType; label: string }> = [
  { value: "slack", label: "Slack" },
  { value: "webhook", label: "Generic Webhook" },
  { value: "email", label: "Email" },
  { value: "teams", label: "Microsoft Teams" },
  { value: "clickup", label: "ClickUp" },
];

const connectorTypeLabel: Record<ConnectorType, string> = {
  slack: "Slack",
  webhook: "Generic Webhook",
  email: "Email",
  teams: "Microsoft Teams",
  clickup: "ClickUp",
};

const slackManifest = `display_information:
  name: Decisively Notifications
  description: Event-driven notifications from Decisively
  background_color: "#00060a"
features:
  bot_user:
    display_name: Decisively
    always_online: false
oauth_config:
  scopes:
    bot:
      - incoming-webhook
      - chat:write
      - files:write
settings:
  org_deploy_enabled: false
  socket_mode_enabled: false
  token_rotation_enabled: false
`;

const webhookExamplePayload = `{
  "connector": "Generic Webhook Connector",
  "sentAt": "2026-03-27T10:00:00.000Z",
  "notification": {
    "title": "Integration failed",
    "summary": "Boomi Payroll Export failed for session s123",
    "body": "The payroll export did not complete successfully.",
    "severity": "error",
    "category": "integration",
    "facts": [
      { "key": "project", "value": "Student Payroll" },
      { "key": "session", "value": "s123" }
    ]
  }
}`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const defaultNameForType = (type: ConnectorType) => `${connectorTypeLabel[type]} Connector`;

const createConnectorId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `connector-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const maskWebhook = (webhookUrl?: string) => {
  if (!webhookUrl) return "Not configured";
  const parts = webhookUrl.split("/");
  if (parts.length < 3) return webhookUrl;
  const token = parts[parts.length - 1];
  const visibleTail = token.slice(-4);
  return `${parts.slice(0, parts.length - 1).join("/")}/****${visibleTail}`;
};

const parseRecipientEmails = (raw: string): string[] => {
  return raw
    .split(/[\n,;]/g)
    .map((item) => item.trim())
    .filter(Boolean);
};

interface EventConnectorsTabProps {
  initialEventId?: SupportedEvent;
  initialEventIds?: SupportedEvent[];
  initialConnectorId?: string;
  dialogOnly?: boolean;
  release?: Release;
  onClose?: () => void;
}

const EventConnectorsTab: React.FC<EventConnectorsTabProps> = ({ initialEventId, initialEventIds, initialConnectorId, dialogOnly = false, release, onClose }) => {
  const notify = useNotify();
  const { form, isLoading } = useSettings();
  const connectors = (useWatch({ control: form.control, name: "connectors" }) || []) as EventConnector[];
  const tenancyId = getActiveTenancy();

  const [isModalOpen, setIsModalOpen] = useState(dialogOnly && !initialConnectorId);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [isPersisting, setIsPersisting] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [lastTestResult, setLastTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [selectedConnectorIndex, setSelectedConnectorIndex] = useState(0);
  const [customEventType, setCustomEventType] = useState("");
  const getInitialSubscriptions = (): SupportedEvent[] => initialEventIds ?? (initialEventId ? [initialEventId] : []);

  const [draft, setDraft] = useState<ConnectorDraft>({
    type: "slack",
    name: defaultNameForType("slack"),
    webhookUrl: "",
    emailToText: "",
    feedbackEmailToText: "",
    clickUpToken: "",
    clickUpListId: "",
    slackBotToken: "",
    slackChannelId: "",
    subscribedEvents: getInitialSubscriptions(),
  });

  const activeModalTitle = useMemo(() => {
    return editingIndex === null ? "Add Event Connector" : "Edit Event Connector";
  }, [editingIndex]);

  const selectedConnector = connectors[selectedConnectorIndex] as EventConnector | undefined;

  const resetModal = () => {
    setEditingIndex(null);
    setIsTesting(false);
    setNameTouched(false);
    setLastTestResult(null);
    setCustomEventType("");
    setDraft({
      type: "slack",
      name: defaultNameForType("slack"),
      webhookUrl: "",
      emailToText: "",
      feedbackEmailToText: "",
      clickUpToken: "",
      clickUpListId: "",
      slackBotToken: "",
      slackChannelId: "",
      subscribedEvents: getInitialSubscriptions(),
    });
  };

  const openCreateModal = () => {
    resetModal();
    setIsModalOpen(true);
  };

  const openEditModal = (index: number) => {
    const connector = connectors[index] as EventConnector;
    const type = connector.type || "slack";
    setEditingIndex(index);
    setNameTouched(true);
    setLastTestResult(null);
    setDraft({
      type,
      name: connector.name || defaultNameForType(type),
      webhookUrl: connector.webhookUrl || "",
      emailToText: (connector.emailTo || []).join(", "),
      feedbackEmailToText: (connector.emailToByEvent?.["feedback.submitted"] || []).join(", "),
      clickUpToken: connector.clickUpToken || "",
      clickUpListId: connector.clickUpListId || "",
      slackBotToken: connector.slackBotToken || "",
      slackChannelId: connector.slackChannelId || "",
      conditions: connector.conditions,
      subscribedEvents: (connector.subscribedEvents || []).filter((event): event is SupportedEvent => typeof event === "string" && Boolean(event.trim())),
    });
    setIsModalOpen(true);
  };

  const openedConnectorId = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!initialConnectorId || isLoading || openedConnectorId.current === initialConnectorId) return;
    openedConnectorId.current = initialConnectorId;
    const index = connectors.findIndex(connector => connector.id === initialConnectorId);
    if (index < 0) {
      notify.error("This connector is no longer available");
      onClose?.();
      return;
    }
    openEditModal(index);
  }, [initialConnectorId, isLoading, connectors, openEditModal, notify, onClose]);

  const closeModal = (force = false) => {
    if (isPersisting && !force) return;
    setIsModalOpen(false);
    resetModal();
    onClose?.();
  };

  const onTypeChange = (nextType: ConnectorType) => {
    setDraft((previous) => {
      const previousDefault = defaultNameForType(previous.type);
      const shouldResetName = !nameTouched || previous.name.trim() === "" || previous.name === previousDefault;

      return {
        ...previous,
        type: nextType,
        name: shouldResetName ? defaultNameForType(nextType) : previous.name,
      };
    });
    setLastTestResult(null);
  };

  const buildConnectorBody = () => {
    const base = {
      type: draft.type,
      name: draft.name.trim(),
      subscribedEvents: draft.subscribedEvents,
      conditions: draft.conditions ?? null,
    } as any;

    if (draft.type === "email") {
      base.emailTo = parseRecipientEmails(draft.emailToText);
      const feedbackRecipients = parseRecipientEmails(draft.feedbackEmailToText);
      if (feedbackRecipients.length > 0) {
        base.emailToByEvent = {
          "feedback.submitted": feedbackRecipients,
        };
      }
    } else if (draft.type === "clickup") {
      base.clickUpToken = draft.clickUpToken.trim();
      base.clickUpListId = draft.clickUpListId.trim();
    } else {
      base.webhookUrl = draft.webhookUrl.trim();
      if (draft.type === "slack") {
        base.slackBotToken = draft.slackBotToken.trim() || undefined;
        base.slackChannelId = draft.slackChannelId.trim() || undefined;
      }
    }

    return base;
  };

  const validateDraft = () => {
    const conditionError = ENABLE_EVENT_CONNECTOR_CONDITIONS && validateEventConditions(draft.conditions);
    if (conditionError) { notify.error(conditionError); return false; }
    const name = draft.name.trim();
    if (!name) {
      notify.error("Connector name is required");
      return false;
    }

    if (draft.subscribedEvents.length === 0) {
      notify.error("Select at least one event subscription");
      return false;
    }

    if (draft.type === "email") {
      const recipients = parseRecipientEmails(draft.emailToText);
      if (recipients.length === 0) {
        notify.error("At least one recipient email is required");
        return false;
      }
      const invalid = recipients.find((email) => !emailRegex.test(email));
      if (invalid) {
        notify.error(`Invalid email address: ${invalid}`);
        return false;
      }
      const feedbackRecipients = parseRecipientEmails(draft.feedbackEmailToText);
      const invalidFeedback = feedbackRecipients.find((email) => !emailRegex.test(email));
      if (invalidFeedback) {
        notify.error(`Invalid feedback email address: ${invalidFeedback}`);
        return false;
      }
      return true;
    }

    if (draft.type === "clickup") {
      if (!draft.clickUpToken.trim()) {
        notify.error("ClickUp API token is required");
        return false;
      }
      if (!draft.clickUpListId.trim()) {
        notify.error("ClickUp List ID is required");
        return false;
      }
      return true;
    }

    const webhookUrl = draft.webhookUrl.trim();
    const hasSlackBotUpload = draft.type === "slack" && draft.slackBotToken.trim() && draft.slackChannelId.trim();
    if (!webhookUrl && !hasSlackBotUpload) {
      const connectorLabel = draft.type === "teams" ? "Microsoft Teams webhook URL" : "Webhook URL";
      notify.error(`${connectorLabel} is required`);
      return false;
    }

    return true;
  };

  const saveConnector = async () => {
    if (!tenancyId) {
      notify.error("Select a tenancy before saving connectors");
      return;
    }

    if (!validateDraft()) {
      return;
    }

    try {
      setIsPersisting(true);
      const body = buildConnectorBody();

      if (editingIndex === null) {
        const response = await createConnector.submit({
          params: { id: tenancyId },
          body: {
            id: createConnectorId(),
            ...body,
          },
        });
        const nextConnectors = [...connectors, response.data];
        form.setValue("connectors", nextConnectors, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      } else {
        const existing = connectors[editingIndex] as EventConnector;
        const response = await updateConnector.submit({
          params: { id: tenancyId, sub_id: existing.id },
          body,
        });
        const nextConnectors = connectors.map((connector, index) =>
          index === editingIndex ? response.data : connector,
        );
        form.setValue("connectors", nextConnectors, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: false,
        });
      }

      notify.success("Connector saved");
      closeModal(true);
    } catch (error: any) {
      notify.error(error?.message || "Failed to save connector");
    } finally {
      setIsPersisting(false);
    }
  };

  const toggleSubscription = (eventId: SupportedEvent) => {
    setDraft((previous) => {
      const isSelected = previous.subscribedEvents.includes(eventId);
      if (isSelected) {
        return {
          ...previous,
          subscribedEvents: previous.subscribedEvents.filter((item) => item !== eventId),
        };
      }
      return {
        ...previous,
        subscribedEvents: [...previous.subscribedEvents, eventId],
      };
    });
  };

  const addCustomSubscription = () => {
    const eventType = customEventType.trim();
    if (!/^[A-Za-z][A-Za-z0-9_.-]{0,127}$/.test(eventType)) {
      notify.error("Use an event type such as payrun.completed");
      return;
    }
    setDraft(previous => previous.subscribedEvents.includes(eventType)
      ? previous
      : { ...previous, subscribedEvents: [...previous.subscribedEvents, eventType] });
    setCustomEventType("");
  };

  const deleteConnectorFromList = async (index: number) => {
    const connector = connectors[index] as EventConnector;
    if (!window.confirm(`Delete connector "${connector.name}"?`)) return;
    if (!tenancyId) {
      notify.error("Select a tenancy before deleting connectors");
      return;
    }

    try {
      setIsPersisting(true);
      await deleteConnector.submit({
        params: { id: tenancyId, sub_id: connector.id },
      });

      const nextConnectors = connectors.filter((_, connectorIndex) => connectorIndex !== index);
      setSelectedConnectorIndex(Math.max(0, Math.min(selectedConnectorIndex, nextConnectors.length - 1)));
      form.setValue("connectors", nextConnectors, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
      notify.success("Connector removed");
    } catch (error: any) {
      notify.error(error?.message || "Failed to remove connector");
    } finally {
      setIsPersisting(false);
    }
  };

  const runTest = async () => {
    if (!tenancyId) {
      notify.error("Select a tenancy before testing connectors");
      return;
    }

    if (!validateDraft()) {
      return;
    }

    try {
      setIsTesting(true);
      setLastTestResult(null);
      const response = await testConnector.submit({
        params: { id: tenancyId },
        body: buildConnectorBody(),
      });

      const message = response.data?.message || "Connector test succeeded";
      setLastTestResult({ success: true, message });
      notify.success(message);
    } catch (error: any) {
      const message = error?.message || "Connector test failed";
      setLastTestResult({ success: false, message });
      notify.error(message);
    } finally {
      setIsTesting(false);
    }
  };

  const copyManifest = async () => {
    try {
      await navigator.clipboard.writeText(slackManifest);
      notify.success("Slack manifest copied");
    } catch (_error) {
      notify.error("Failed to copy manifest");
    }
  };

  const downloadManifest = () => {
    const blob = new Blob([slackManifest], { type: "text/yaml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "decisively-slack-manifest.yaml";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const downloadLogo = async () => {
    try {
      const response = await fetch("/logo.svg");
      if (!response.ok) throw new Error("Failed to fetch logo");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = "decisively-logo.svg";
      anchor.click();
      URL.revokeObjectURL(url);
      notify.success("Downloaded decisively-logo.svg");
    } catch (_error) {
      notify.error("Failed to download decisively-logo.svg");
    }
  };

  const renderInstructions = () => {
    if (draft.type === "slack") {
      return (
        <div className="space-y-3">
          <h4 className="font-semibold mb-2 text-textSecondary">Slack Setup Instructions</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-textSecondary">
            <li>Open Slack API and create a new app in your workspace.</li>
            <li>
              Choose "From manifest" and paste the manifest below, or create manually and enable Incoming Webhooks.
            </li>
            <li>Create a new webhook and select the channel that should receive notifications.</li>
            <li>Copy the generated webhook URL and paste it into this form.</li>
            <li>For feedback attachments, install the app and paste the bot token plus destination channel ID.</li>
            <li>Use Test to verify delivery before saving.</li>
          </ol>
          <p className="mt-2 text-sm text-textSecondary">
            Keep webhook URLs and bot tokens secret. Webhook-only Slack connectors receive feedback summaries without
            files.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={copyManifest}
                label="Copy Manifest"
              >
                Copy Manifest
              </Button>
              <Button
                onClick={downloadManifest}
                label="Download Manifest"
              >
                Download Manifest
              </Button>
              <Button
                onClick={downloadLogo}
                label="Download Logo"
              >
                Download Logo
              </Button>
            </div>
            <div className="inline-flex items-center gap-2 rounded-md border border-blue-300 bg-slate-900 px-3 py-2 text-xs text-blue-100">
              <img
                src="/logo.svg"
                alt="Decisively logo preview"
                className="h-5 w-auto"
              />
              <span>Preview of app logo</span>
            </div>
            <pre className="text-xs bg-white border border-blue-200 rounded-md p-3 overflow-x-auto text-textSecondary">
              {slackManifest}
            </pre>
          </div>
        </div>
      );
    }

    if (draft.type === "webhook") {
      return (
        <div className="space-y-3">
          <h4 className="font-semibold mb-2 text-textSecondary">Generic Webhook Setup</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-textSecondary">
            <li>Create or identify an HTTP endpoint in your target system.</li>
            <li>Allow POST requests with JSON payloads from Decisively.</li>
            <li>Paste the endpoint URL into this form.</li>
            <li>Use Test to confirm your endpoint receives requests.</li>
          </ol>
          <p className="mt-2 text-sm text-textSecondary">
            Your endpoint should return 2xx for successful receipt. Feedback events include metadata only; logs,
            screenshots, and exports are not posted to generic webhooks.
          </p>
          <pre className="mt-3 text-xs bg-white border border-blue-200 rounded-md p-3 overflow-x-auto text-textSecondary">
            {webhookExamplePayload}
          </pre>
        </div>
      );
    }

    if (draft.type === "teams") {
      return (
        <div className="space-y-3">
          <h4 className="font-semibold mb-2 text-textSecondary">Microsoft Teams Setup</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-textSecondary">
            <li>In Teams, open the channel where you want notifications.</li>
            <li>Add an Incoming Webhook app or create a Teams workflow webhook endpoint.</li>
            <li>Name the webhook and optionally set an icon.</li>
            <li>Copy the webhook URL and paste it into this form.</li>
            <li>Use Test to verify the connector before saving.</li>
          </ol>
          <p className="mt-2 text-sm text-textSecondary">
            Add the webhook directly to the destination channel so notifications appear in the right place. Feedback
            artifacts are not attached to Teams messages in this version.
          </p>
        </div>
      );
    }

    if (draft.type === "clickup") {
      return (
        <div className="space-y-3">
          <h4 className="font-semibold mb-2 text-textSecondary">ClickUp Setup</h4>
          <ol className="list-decimal list-inside space-y-1 text-sm text-textSecondary">
            <li>Create or choose a ClickUp API token with access to the target workspace.</li>
            <li>Open the destination List in ClickUp and copy its List ID.</li>
            <li>Paste the API token and List ID into this form.</li>
            <li>Subscribe the connector to feedback.submitted to create feedback tasks.</li>
            <li>Use Test to confirm Decisively can read the configured List.</li>
          </ol>
          <p className="mt-2 text-sm text-textSecondary">
            Feedback creates a task in the configured List and uploads available artifacts as task attachments.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        <h4 className="font-semibold mb-2 text-textSecondary">Email Connector Setup</h4>
        <ol className="list-decimal list-inside space-y-1 text-sm text-textSecondary">
          <li>Enter one or more default recipient email addresses in the connector form.</li>
          <li>Optionally enter feedback-specific recipients to override the defaults for feedback.submitted.</li>
          <li>Ensure your Decisively environment has email delivery configured.</li>
          <li>Use Test to send a sample notification email before saving.</li>
        </ol>
        <p className="mt-2 text-sm text-textSecondary">Recipients can be separated by commas or new lines.</p>
      </div>
    );
  };

  const renderConnectorSummary = (connector: EventConnector) => {
    const type = (connector.type || "slack") as ConnectorType;
    if (type === "email") {
      return (connector.emailTo || []).length > 0 ? connector.emailTo?.join(", ") : "No recipients configured";
    }
    if (type === "clickup") return connector.clickUpListId || "No list configured";
    return maskWebhook(connector.webhookUrl);
  };

  const selectedSubscribedEvents = selectedConnector?.subscribedEvents || [];

  return (
    <>
      {!dialogOnly && <div className="flex h-full min-h-[640px] flex-col overflow-hidden bg-white">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <h2 className="text-xl font-semibold">Event Connectors</h2>
          <p className="text-sm text-textSecondary">
            Connector create, edit, and delete actions are saved immediately.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <EventTypesButton />
          <Button
            type="button"
            onClick={openCreateModal}
            disabled={isPersisting}
            label="Add connector"
          >
            <Plus size={18} />
            Add connector
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <SplitPane
          direction="horizontal"
          gutterSize={6}
        >
          <SplitPane.CollapsiblePane
            minSize={240}
            preferredSize="360px"
            header="Connectors"
          >
            <ItemList
              className="h-full"
              items={connectors}
              getItemKey={(connector, index) => connector.id || `${connector.name}-${index}`}
              renderEmpty={() => (
                <div className="p-4 text-sm text-textSecondary">No event connectors configured yet.</div>
              )}
              renderItem={(connector, index) => {
                const type = (connector.type || "slack") as ConnectorType;
                return (
                  <ItemList.Item
                    key={connector.id || `${connector.name}-${index}`}
                    selected={selectedConnectorIndex === index}
                    onClick={() => setSelectedConnectorIndex(index)}
                  >
                    <ItemList.Left>
                      <div className="flex min-w-0 flex-col overflow-hidden">
                        <ItemList.Title>{connector.name || defaultNameForType(type)}</ItemList.Title>
                        <div className="text-[10px] text-textSecondary">{connectorTypeLabel[type]}</div>
                        <div className="truncate text-[10px] text-textSecondary">
                          {renderConnectorSummary(connector)}
                        </div>
                      </div>
                    </ItemList.Left>
                  </ItemList.Item>
                );
              }}
            />
          </SplitPane.CollapsiblePane>

          <SplitPane.Pane>
            {selectedConnector ? (
              <div className="flex h-full flex-col overflow-hidden bg-white">
                <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-wide text-textSecondary">
                      {connectorTypeLabel[(selectedConnector.type || "slack") as ConnectorType]}
                    </div>
                    <h3 className="truncate text-2xl font-semibold">{selectedConnector.name}</h3>
                    <p className="mt-1 text-sm text-textSecondary">{renderConnectorSummary(selectedConnector)}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => openEditModal(selectedConnectorIndex)}
                      label="Edit connector"
                      disabled={isPersisting}
                    >
                      <Pencil size={18} />
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      intent="destructive"
                      onClick={() => deleteConnectorFromList(selectedConnectorIndex)}
                      label="Delete connector"
                      disabled={isPersisting}
                    >
                      <Trash2 size={18} />
                      Delete
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-auto p-6">
                  <div className="grid max-w-4xl grid-cols-1 gap-6 lg:grid-cols-2">
                    <section className="rounded-lg border border-border bg-card p-4">
                      <h4 className="font-semibold">Configuration</h4>
                      <dl className="mt-4 space-y-3 text-sm">
                        <div>
                          <dt className="text-textSecondary">Type</dt>
                          <dd>{connectorTypeLabel[(selectedConnector.type || "slack") as ConnectorType]}</dd>
                        </div>
                        <div>
                          <dt className="text-textSecondary">Destination</dt>
                          <dd className="break-all">{renderConnectorSummary(selectedConnector)}</dd>
                        </div>
                      </dl>
                    </section>

                    <section className="rounded-lg border border-border bg-card p-4">
                      <h4 className="font-semibold">Event subscriptions</h4>
                      {selectedSubscribedEvents.length > 0 ? (
                        <div className="mt-3 space-y-3">
                          {selectedSubscribedEvents.map((eventId) => {
                            const event = supportedEvents.find((item) => item.id === eventId);
                            return (
                            <div
                              key={eventId}
                              className="rounded-md border border-border bg-white p-3"
                            >
                              <div className="text-sm font-medium">{getEventTypeDisplayName(eventId)}</div>
                              <div className="text-xs text-textSecondary">{eventId}</div>
                              {event && <div className="mt-1 text-sm text-textSecondary">{event.description}</div>}
                            </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-textSecondary">No tenancy-wide events selected.</p>
                      )}
                    </section>
                  </div>
                </div>
              </div>
            ) : (
              <EmptyOverlay message="Select a connector or add a new one" />
            )}
          </SplitPane.Pane>
        </SplitPane>
      </div>



      </div>}
      <Dialog
        open={isModalOpen}
        intent="cover"
        onOpenChange={(open) => !open && closeModal()}
      >
          <Dialog.Content>
            <DialogHeader title={activeModalTitle} />
            <AdapterResourceOwnership kind="connector" resourceId={editingIndex === null ? undefined : connectors[editingIndex]?.id} release={release} className="flex justify-end px-6 pb-2" onNavigate={() => closeModal()} />

            <Dialog.Body className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                <FormPanel>
                  <FormPanel className="py-4">
                    <div className="mb-5">
                      <FormPanel.Title>Connector details</FormPanel.Title>
                      <p className="mt-1 text-sm text-textSecondary">
                        Give this connector a name operators will recognise.
                      </p>
                    </div>

                    <div className="flex flex-col gap-5">
                      <TextField
                        name="event-connector-name"
                        label="Name"
                        value={draft.name}
                        onValueChange={(name) => {
                          setNameTouched(true);
                          setDraft((previous) => ({ ...previous, name }));
                        }}
                        placeholder={defaultNameForType(draft.type)}
                        fullWidth
                      />
                    </div>
                  </FormPanel>

                  {ENABLE_EVENT_CONNECTOR_CONDITIONS && <EventConditionEditor events={draft.subscribedEvents} value={draft.conditions}
                    onChange={conditions => setDraft(previous => ({ ...previous, conditions }))} />}

                  <FormPanel className="border-t border-border py-4">
                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <FormPanel.Title>Event subscriptions</FormPanel.Title>
                        <p className="mt-1 text-sm text-textSecondary">
                          Select the events this connector should receive.
                        </p>
                      </div>
                      <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-textSecondary">
                        {draft.subscribedEvents.length} selected
                      </span>
                    </div>

                    <div className="flex flex-col gap-6">
                      {supportedEventGroups.map(group => (
                        <fieldset key={group.category} className="min-w-0">
                          <legend className="mb-3 text-sm font-semibold capitalize">{group.label}</legend>
                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            {group.events.map((event) => {
                              const selected = draft.subscribedEvents.includes(event.id);
                              return (
                                <Button
                                  key={event.id}
                                  type="button"
                                  variant="outline"
                                  label={event.name}
                                  aria-pressed={selected}
                                  aria-describedby={`event-subscription-${event.id}-description`}
                                  onClick={() => toggleSubscription(event.id)}
                                  className={`h-auto min-w-0 items-start justify-start gap-3 whitespace-normal rounded-lg p-4 text-left transition-colors ${
                                    selected ? "border-primary bg-primary-subtle hover:bg-primary/20" : "border-border hover:bg-muted"
                                  }`}
                                >
                                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${selected ? "bg-primary text-primary-foreground" : "bg-muted text-textSecondary"}`}>
                                    <EventIcon eventType={event.id} />
                                  </span>
                                  <span className="min-w-0 flex-1">
                                    <span className="block font-semibold">{event.name}</span>
                                    <code className="mt-1 block break-all text-xs font-normal text-textSecondary">{event.id}</code>
                                    <span id={`event-subscription-${event.id}-description`} className="mt-1 block text-sm font-normal text-textSecondary">
                                      {event.description}
                                    </span>
                                  </span>
                                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${selected ? "border-primary bg-primary text-primary-foreground" : "border-border"}`} aria-hidden="true">
                                    {selected ? <Check size={14} /> : null}
                                  </span>
                                </Button>
                              );
                            })}
                          </div>
                        </fieldset>
                      ))}
                    </div>

                    <Collapsible className="mt-6 rounded-lg border border-border">
                      <CollapsibleTrigger asChild>
                        <Button type="button" variant="ghost" label="Other events"
                          className="group w-full justify-between p-4">
                          <span>Other events ({otherEvents.filter(event => draft.subscribedEvents.includes(event.id)).length} selected)</span>
                          <ChevronDown size={16} className="transition-transform group-data-[state=open]:rotate-180" />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="px-4 pb-4">
                        <p className="mb-3 text-sm text-textSecondary">
                          Subscribe to additional events, including mapping selection and lifecycle updates.
                        </p>
                        <div className="divide-y divide-border">
                          {otherEvents.map(event => (
                            <div key={event.id} className="flex min-w-0 items-start gap-3 py-3">
                              <EventIcon eventType={event.id} className="mt-1 shrink-0 text-textSecondary" />
                              <div className="min-w-0 flex-1">
                                <SwitchField
                                  label={event.name}
                                  value={draft.subscribedEvents.includes(event.id)}
                                  onValueChange={() => toggleSubscription(event.id)}
                                  helperText={event.description}
                                />
                                <code className="mt-1 block break-all text-xs text-textSecondary">{event.id}</code>
                              </div>
                            </div>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                    <div className="mt-6 rounded-lg border border-border p-4">
                      <h4 className="font-semibold">Custom event</h4>
                      <p className="mt-1 text-sm text-textSecondary">Add an event type emitted by another service, such as <code>payrun.completed</code>.</p>
                      <div className="mt-3 flex gap-2">
                        <TextField name="custom-event-type" label="Event type" value={customEventType} onValueChange={setCustomEventType}
                          placeholder="payrun.completed" fullWidth />
                        <Button type="button" variant="outline" className="self-end" label="Add custom event" onClick={addCustomSubscription}>Add</Button>
                      </div>
                      {draft.subscribedEvents.filter(eventId => !supportedEvents.some(event => event.id === eventId)).map(eventId => (
                        <div key={eventId} className="mt-3 flex items-center justify-between gap-3 rounded border border-border px-3 py-2">
                          <code className="break-all text-sm">{getEventTypeDisplayName(eventId)}</code>
                          <Button type="button" size="sm" variant="ghost" label={`Remove ${eventId}`} onClick={() => toggleSubscription(eventId)}><Trash2 size={14} /></Button>
                        </div>
                      ))}
                    </div>
                  </FormPanel>

                  {lastTestResult ? (
                    <div
                      className={`rounded-lg border p-4 text-sm ${
                        lastTestResult.success
                          ? "border-green-300 bg-green-50 text-green-800"
                          : "border-red-300 bg-red-50 text-red-800"
                      }`}
                    >
                      {lastTestResult.message}
                    </div>
                  ) : null}
                </FormPanel>
                <FormPanel className="lg:border-l lg:border-border lg:pl-8">
                  <FormPanel className="py-4">
                    <div className="mb-5">
                      <FormPanel.Title>Destination</FormPanel.Title>
                      <p className="mt-1 text-sm text-textSecondary">
                        Choose the destination type and enter its connection details.
                      </p>
                    </div>

                    <div className="mb-5">
                      <SelectField
                        name="event-connector-type"
                        label="Type"
                        value={draft.type}
                        onValueChange={(value) => value && onTypeChange(value as ConnectorType)}
                        options={connectorTypeOptions}
                        searchable={false}
                        fullWidth
                      />
                    </div>

                    {draft.type === "email" ? (
                      <div className="space-y-5">
                        <TextField
                          name="event-connector-email-to"
                          label="Default recipient emails"
                          value={draft.emailToText}
                          onValueChange={(emailToText) => setDraft((previous) => ({ ...previous, emailToText }))}
                          placeholder="ops@example.com, oncall@example.com"
                          helperText="Separate multiple recipients with commas or new lines."
                          inputComponent={TextArea}
                          rows={3}
                          fullWidth
                        />
                        <TextField
                          name="event-connector-feedback-email-to"
                          label="Feedback recipient override"
                          value={draft.feedbackEmailToText}
                          onValueChange={(feedbackEmailToText) =>
                            setDraft((previous) => ({ ...previous, feedbackEmailToText }))
                          }
                          placeholder="feedback@example.com"
                          helperText="Optional. Used only for feedback.submitted."
                          inputComponent={TextArea}
                          rows={3}
                          fullWidth
                        />
                      </div>
                    ) : draft.type === "clickup" ? (
                      <div className="flex flex-col gap-5">
                        <TextField
                          name="event-connector-clickup-token"
                          label="ClickUp API token"
                          value={draft.clickUpToken}
                          onValueChange={(clickUpToken) => setDraft((previous) => ({ ...previous, clickUpToken }))}
                          placeholder="pk_..."
                          fullWidth
                        />
                        <TextField
                          name="event-connector-clickup-list-id"
                          label="Default list ID"
                          value={draft.clickUpListId}
                          onValueChange={(clickUpListId) => setDraft((previous) => ({ ...previous, clickUpListId }))}
                          placeholder="901234567890"
                          fullWidth
                        />
                      </div>
                    ) : (
                      <div className="space-y-5">
                        <TextField
                          name="event-connector-webhook-url"
                          label={
                            draft.type === "slack"
                              ? "Slack webhook URL"
                              : draft.type === "teams"
                                ? "Microsoft Teams webhook URL"
                                : "Webhook URL"
                          }
                          value={draft.webhookUrl}
                          onValueChange={(webhookUrl) => setDraft((previous) => ({ ...previous, webhookUrl }))}
                          placeholder={
                            draft.type === "slack"
                              ? "https://hooks.slack.com/services/..."
                              : draft.type === "teams"
                                ? "https://.../webhook/..."
                                : "https://example.com/hooks/decisively"
                          }
                          fullWidth
                        />
                        {draft.type === "slack" ? (
                          <div className="flex flex-col gap-5">
                            <TextField
                              name="event-connector-slack-bot-token"
                              label="Slack bot token"
                              value={draft.slackBotToken}
                              onValueChange={(slackBotToken) =>
                                setDraft((previous) => ({ ...previous, slackBotToken }))
                              }
                              placeholder="xoxb-..."
                              helperText="Optional. Required only for feedback attachments."
                              fullWidth
                            />
                            <TextField
                              name="event-connector-slack-channel-id"
                              label="Slack channel ID"
                              value={draft.slackChannelId}
                              onValueChange={(slackChannelId) =>
                                setDraft((previous) => ({ ...previous, slackChannelId }))
                              }
                              placeholder="C0123456789"
                              helperText="Optional. Pair with bot token for file uploads."
                              fullWidth
                            />
                          </div>
                        ) : null}
                      </div>
                    )}
                  </FormPanel>

                  <FormPanel className="border-t border-border py-4">
                    <FormPanel.Title>Setup instructions</FormPanel.Title>
                    {renderInstructions()}
                  </FormPanel>
                </FormPanel>
              </div>
            </Dialog.Body>

            <Dialog.Actions>
              <Dialog.Actions.CancelButton
                disabled={isPersisting}
                label="Cancel"
              />
              <Dialog.Actions.SecondaryButton
                label={isTesting ? "Testing..." : "Test"}
                onClick={runTest}
                disabled={isTesting || isPersisting}
              >
                {isTesting ? "Testing..." : "Test"}
              </Dialog.Actions.SecondaryButton>
              <Dialog.Actions.PrimaryButton
                label={isPersisting ? "Saving..." : "Save Connector"}
                onClick={saveConnector}
                disabled={isPersisting || isTesting}
              >
                {isPersisting ? "Saving..." : "Save Connector"}
              </Dialog.Actions.PrimaryButton>
            </Dialog.Actions>
          </Dialog.Content>
      </Dialog>
    </>
  );
};

export default EventConnectorsTab;
